import { useReducer, useCallback, useMemo } from 'react';
import type { InvoiceLineRow, CounterCandidate } from '../types/marginCorrections';

/**
 * The correction draft — the model behind the Correct-Cost modal.
 *
 * EVERYTHING here is integer CENTS. A correction is valid only when the deltas sum to EXACTLY
 * zero, and a float sum (0.1 + 0.2 !== 0.3) can never prove that. The API re-checks in cents and
 * a DB trigger checks a third time, but the Save button must not even offer to submit an
 * unbalanced group — so the same cent arithmetic lives here too.
 *
 * Sign convention: `deltaCents` is the change to GROSS PROFIT. Lowering a line's cost RAISES its
 * gross profit, so "reduce this line's cost by 50" is deltaCents = +5000 on that line and a
 * matching -5000 spread across the counter-lines.
 */

export interface DraftLine {
    documentID: string;
    documentCode: string;
    documentLineNr: number;
    customerName: string;
    productName: string;
    postingDate: string;
    quantity: number;
    netSalesCents: number;
    /** BC's booked gross profit, in cents — the baseline the preview restates. */
    baseGrossProfitCents: number;
    /** The correction on THIS line's gross profit, in cents. May be negative. */
    deltaCents: number;
    /** True for the line the user started from (the one being corrected); false for counter-lines. */
    isSource: boolean;
    lineNote: string;
}

export interface DraftState {
    lines: DraftLine[];
}

type Action =
    | { type: 'init'; source: InvoiceLineRow }
    | { type: 'addCounter'; candidate: CounterCandidate }
    | { type: 'removeLine'; documentID: string }
    | { type: 'setDelta'; documentID: string; deltaCents: number }
    | { type: 'setNote'; documentID: string; note: string }
    | { type: 'splitRemaining' }
    | { type: 'reset' };

export const toCents = (eur: number): number => Math.round(eur * 100);
export const toEur = (cents: number): number => cents / 100;

function lineFromInvoice(row: InvoiceLineRow, isSource: boolean, deltaCents: number): DraftLine {
    return {
        documentID: row.documentID,
        documentCode: row.documentCode,
        documentLineNr: row.documentLineNr,
        customerName: row.customerName,
        productName: row.productName,
        postingDate: row.postingDate,
        quantity: row.quantity,
        netSalesCents: toCents(row.netSalesEUR),
        // The correction is additive on top of whatever BC currently reports, so the baseline is
        // the ALREADY-CORRECTED gross profit, not the original. Correcting twice stacks correctly.
        baseGrossProfitCents: toCents(row.grossProfitEUR),
        deltaCents,
        isSource,
        lineNote: '',
    };
}

function lineFromCandidate(c: CounterCandidate): DraftLine {
    return {
        documentID: c.documentID,
        documentCode: c.documentCode,
        documentLineNr: c.documentLineNr,
        customerName: c.customerName,
        productName: c.productName,
        postingDate: c.postingDate,
        quantity: c.quantity,
        netSalesCents: toCents(c.netSalesEUR),
        baseGrossProfitCents: toCents(c.grossProfitEUR),
        deltaCents: 0,
        isSource: false,
        lineNote: '',
    };
}

function reducer(state: DraftState, action: Action): DraftState {
    switch (action.type) {
        case 'init':
            return { lines: [lineFromInvoice(action.source, true, 0)] };

        case 'addCounter': {
            if (state.lines.some(l => l.documentID === action.candidate.documentID)) return state;
            return { lines: [...state.lines, lineFromCandidate(action.candidate)] };
        }

        case 'removeLine':
            // The source line is the anchor of the correction and cannot be removed.
            return { lines: state.lines.filter(l => l.documentID !== action.documentID || l.isSource) };

        case 'setDelta':
            return {
                lines: state.lines.map(l =>
                    l.documentID === action.documentID ? { ...l, deltaCents: action.deltaCents } : l
                ),
            };

        case 'setNote':
            return {
                lines: state.lines.map(l =>
                    l.documentID === action.documentID ? { ...l, lineNote: action.note } : l
                ),
            };

        case 'splitRemaining': {
            // Distribute the residual across the COUNTER-lines so the group nets to zero. The
            // residual is what the counter-lines must jointly absorb: -(sum of all deltas). Split
            // it as evenly as cents allow and drop the indivisible remainder on the last one, so
            // the sum is exact — never "off by a cent".
            const counters = state.lines.filter(l => !l.isSource);
            if (counters.length === 0) return state;

            const residual = -state.lines.reduce((s, l) => s + l.deltaCents, 0);
            const per = Math.trunc(residual / counters.length);
            const remainder = residual - per * counters.length;

            let idx = 0;
            return {
                lines: state.lines.map(l => {
                    if (l.isSource) return l;
                    const isLast = idx === counters.length - 1;
                    const add = per + (isLast ? remainder : 0);
                    idx++;
                    return { ...l, deltaCents: l.deltaCents + add };
                }),
            };
        }

        case 'reset':
            return { lines: [] };

        default:
            return state;
    }
}

export function useCorrectionDraft() {
    const [state, dispatch] = useReducer(reducer, { lines: [] });

    const init = useCallback((source: InvoiceLineRow) => dispatch({ type: 'init', source }), []);
    const addCounter = useCallback((candidate: CounterCandidate) => dispatch({ type: 'addCounter', candidate }), []);
    const removeLine = useCallback((documentID: string) => dispatch({ type: 'removeLine', documentID }), []);
    const setDeltaEur = useCallback((documentID: string, eur: number) => dispatch({ type: 'setDelta', documentID, deltaCents: toCents(eur) }), []);
    const setNote = useCallback((documentID: string, note: string) => dispatch({ type: 'setNote', documentID, note }), []);
    const splitRemaining = useCallback(() => dispatch({ type: 'splitRemaining' }), []);
    const reset = useCallback(() => dispatch({ type: 'reset' }), []);

    const derived = useMemo(() => {
        const residualCents = state.lines.reduce((s, l) => s + l.deltaCents, 0);
        const source = state.lines.find(l => l.isSource) ?? null;
        const counters = state.lines.filter(l => !l.isSource);
        const hasPositive = state.lines.some(l => l.deltaCents > 0);
        const hasNegative = state.lines.some(l => l.deltaCents < 0);

        // A resulting cost below zero is nonsensical (the line would show negative COGS). Cost =
        // NetSales - GrossProfit, and GP moves by +delta, so cost moves by -delta.
        const wouldGoNegative = state.lines.filter(l => {
            const newCost = l.netSalesCents - (l.baseGrossProfitCents + l.deltaCents);
            return newCost < 0;
        });

        const isBalanced = residualCents === 0;
        const isSubmittable =
            isBalanced &&
            state.lines.length >= 2 &&
            state.lines.every(l => Number.isFinite(l.deltaCents)) &&
            state.lines.some(l => l.deltaCents !== 0) &&
            hasPositive &&
            hasNegative &&
            wouldGoNegative.length === 0;

        return { residualCents, source, counters, isBalanced, isSubmittable, wouldGoNegative, hasPositive, hasNegative };
    }, [state.lines]);

    return { lines: state.lines, ...derived, init, addCounter, removeLine, setDeltaEur, setNote, splitRemaining, reset };
}
